import { getPageLayoutOptions, normalizePageDescription, normalizePageTitle } from '../../pages';
import type { PageLayoutOptions, Revision, RevisionPage, RevisionPageIconProps } from '../../types';
import { isYValue, jsonToY, mutations, yToJSON } from '../../utils';
import { isRevisionPageDocument } from './guards';

/**
 * Update a page in a revision.
 */
export function updatePageInRevision(
    revision: Revision,
    pageId: string,
    update: Partial<RevisionPage> | ((page: RevisionPage) => void)
): void;
export function updatePageInRevision(
    revision: mutations.MutableData<Revision>,
    pageId: string,
    update: Partial<RevisionPage> | ((page: mutations.MutableData<RevisionPage>) => void)
): void;
export function updatePageInRevision(
    revision: mutations.MutableData<Revision>,
    pageId: string,
    update: Partial<RevisionPage> | ((page: mutations.MutableData<RevisionPage>) => void)
): void {
    const prevPage = mutations.getIn(revision, ['pages', pageId] as const);

    if (!prevPage) {
        return;
    }

    if (typeof update === 'function') {
        update(prevPage);
        return;
    }

    Object.entries(update).forEach(([key, value]) => {
        mutations.set(
            prevPage,
            key as keyof RevisionPage,
            isYValue(revision) ? jsonToY(value) : value
        );
    });
}

/**
 * Update layout properties of a page in a revision.
 */
export function updatePageLayoutInRevision(
    revision: mutations.MutableData<Revision>,
    pageId: string,
    update: Partial<PageLayoutOptions>
): void {
    updatePageInRevision(revision, pageId, (page) => {
        if (!isRevisionPageDocument(page)) {
            return;
        }

        const options = getPageLayoutOptions(yToJSON(page));
        mutations.set(page, 'layout', {
            ...options,
            ...update,
        });
    });
}

/**
 * Update the title of a page in a revision.
 */
export function renamePageInRevision(
    revision: mutations.MutableData<Revision>,
    pageId: string,
    title: string
): void {
    updatePageInRevision(revision, pageId, { title: normalizePageTitle(title) });
}

/**
 * Update the description of a page in a revision.
 */
export function setPageDescriptionInRevision(
    revision: mutations.MutableData<Revision>,
    pageId: string,
    description: string
): void {
    updatePageInRevision(revision, pageId, (page) => {
        if (isRevisionPageDocument(page)) {
            mutations.set(page, 'description', normalizePageDescription(description));
        }
    });
}

/**
 * Update the icon/emoji of a page in a revision.
 */
export function setPageIconInRevision(
    revision: Revision,
    pageId: string,
    update: RevisionPageIconProps
): void;
export function setPageIconInRevision(
    revision: mutations.MutableData<Revision>,
    pageId: string,
    update: RevisionPageIconProps
): void;
export function setPageIconInRevision(
    revision: mutations.MutableData<Revision>,
    pageId: string,
    update: RevisionPageIconProps
): void {
    updatePageInRevision(revision, pageId, (page) => {
        if (update.icon) {
            mutations.set(page, 'icon', update.icon);
            mutations.del(page, 'emoji');
        } else if (update.emoji) {
            mutations.set(page, 'emoji', update.emoji);
            mutations.del(page, 'icon');
        } else {
            mutations.del(page, 'icon');
            mutations.del(page, 'emoji');
        }
    });
}
